import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { IntegratorSitePreset, SitePresetRequest, SiteService } from '@wheere/api-site';
import { SiteFile, SiteFileTemplate, SitePreset, UpdateSiteLayoutBody } from './site.model';
import { SITEMANAGER_BASE_PATH } from './sitemanager-base-path.token';

/**
 * V7 sitemanager client — only the lifecycle/template/file ops added in V7. Existing CRUD on
 * `/sites` keeps using the generated `@wheere/api-site`; this service is purely additive so
 * apps can ship the V7 flow before the generated client is regenerated.
 */
@Injectable({ providedIn: 'root' })
export class SitesV7HttpClient {
  #http = inject(HttpClient);
  #basePath = inject(SITEMANAGER_BASE_PATH);
  #siteService = inject(SiteService);

  // ─────────────────────── Integrator
  // The V7 lifecycle (`requestValidation`, validation queue, `getDomainSite`) was retired
  // with V8 + presets, and there is no admin review of a site either (2026-10-07).

  // Read existing files (works for both integrator + admin — admin gates upload separately)
  listFiles(siteId: string): Promise<SiteFile[]> {
    return firstValueFrom(this.#http.get<SiteFile[]>(`${this.#basePath}/sites/${siteId}/files`));
  }

  /**
   * Build a downloadable Blob for a single site file. There is no dedicated `/download`
   * endpoint on the backend (see SiteFileController — it only has `GET /` for the listing
   * and `PUT /{kind}` for admin uploads). The list endpoint already returns each file's
   * `rawContent`, so we pull that and wrap it in a Blob client-side.
   *
   * <p>Accepts both the suffixed kind (`calib_file`) and the bare alias (`calib`) the UI uses.
   * Throws if the file isn't attached to the site (e.g. nothing has been uploaded yet).
   */
  async downloadFile(siteId: string, kind: string): Promise<{ blob: Blob; filename: string }> {
    const wanted = kind.endsWith('_file') ? kind : `${kind}_file`;
    const files = await this.listFiles(siteId);
    const match = files.find(f => f.fileType === wanted || f.fileType === kind);
    if (!match) {
      throw new Error(`No ${kind} file attached to site ${siteId}`);
    }
    const content = match.rawContent ?? '';
    return {
      blob: new Blob([content], { type: 'text/plain;charset=utf-8' }),
      filename: match.fileName ?? `${siteId}-${kind}.txt`,
    };
  }

  // ─────────────────────── Admin: technical files (PUT /sites/{id}/files/{kind})

  uploadAdaptedFile(siteId: string, kind: string, file: File, adaptedForGatewayCount?: number): Promise<SiteFile> {
    const form = new FormData();
    form.append('file', file);
    const params: Record<string, string> = {};
    if (adaptedForGatewayCount != null) {
      params['adaptedForGatewayCount'] = String(adaptedForGatewayCount);
    }
    return firstValueFrom(
      this.#http.put<SiteFile>(`${this.#basePath}/sites/${siteId}/files/${kind}`, form, {
        params,
      })
    );
  }

  // ─────────────────────── Admin: templates

  listTemplates(): Promise<SiteFileTemplate[]> {
    return firstValueFrom(this.#http.get<SiteFileTemplate[]>(`${this.#basePath}/site-templates`));
  }

  /**
   * ADR-019 — templates are no longer scoped by site type: one baseline per {@code fileKind}.
   * {@code PUT /site-templates/files/{fileKind}} (api-site 5.0.0).
   */
  upsertTemplate(fileKind: string, file: File): Promise<SiteFileTemplate> {
    const form = new FormData();
    form.append('file', file);
    return firstValueFrom(this.#http.put<SiteFileTemplate>(`${this.#basePath}/site-templates/files/${fileKind}`, form));
  }

  /**
   * Wipe the current configuration of a site (gateways + antennas).
   * The integrator triggers this via a danger button in the Configuration tab when they
   * want to start over from scratch.
   */
  async cleanSite(siteId: string): Promise<unknown> {
    return lastValueFrom(this.#siteService.cleanSite(siteId));
  }

  /**
   * Permanently delete a site. The backend cleans the configuration first (detaches gateways /
   * trackers / antennas) and then removes the site row. ORG_ADMIN-gated in the integrator UI;
   * the backend enforces org membership ({@code PLATFORM_OR_ORG}).
   */
  async deleteSite(siteId: string): Promise<unknown> {
    return lastValueFrom(this.#siteService.deleteSite(siteId));
  }

  // ─────────────────────── V8: layout save

  /**
   * Layout save — the ONLY configuration path since ADR-019 (the {@code POST /sites/{id}/validate*}
   * endpoints are gone). PATCH the positions / preset / size / comment and the backend
   * republishes MQTT immediately.
   */
  saveLayout(siteId: string, body: UpdateSiteLayoutBody): Promise<void> {
    return firstValueFrom(this.#http.patch<void>(`${this.#basePath}/sites/${siteId}/layout`, body));
  }

  // ─────────────────────── V8: per-org preset catalog

  listPresets(organizationId: string): Promise<SitePreset[]> {
    return firstValueFrom(
      this.#http.get<SitePreset[]>(`${this.#basePath}/site-presets`, { params: { organizationId } })
    );
  }

  /**
   * Integrator-safe preset listing. Hits the slim {@code /integrator/site-presets} endpoint that
   * NEVER ships the raw freq/calib/theta blobs (Wheere calibration IP) — it returns file names +
   * a precomputed {@code freqCoverage} only. ORG users must use this; the full {@link listPresets}
   * is for the PLATFORM_ADMIN catalog page in wheere-inside.
   */
  listIntegratorPresets(organizationId: string): Promise<IntegratorSitePreset[]> {
    return firstValueFrom(
      this.#http.get<IntegratorSitePreset[]>(`${this.#basePath}/integrator/site-presets`, {
        params: { organizationId },
      })
    );
  }

  /** Integrator-safe single preset (slim shape) — used to hydrate a site's active preset on
   * load without mounting the picker, so the Overview reflects it immediately. */
  getIntegratorPreset(presetId: string): Promise<IntegratorSitePreset> {
    return firstValueFrom(
      this.#http.get<IntegratorSitePreset>(`${this.#basePath}/integrator/site-presets/${presetId}`)
    );
  }

  getPreset(presetId: string): Promise<SitePreset> {
    return firstValueFrom(this.#http.get<SitePreset>(`${this.#basePath}/site-presets/${presetId}`));
  }

  /** ADR-039 : fichier de fréquences et calib envoyés tels que lus ; refus 422 `PRESET_REJECTED`. */
  createPreset(body: SitePresetRequest): Promise<SitePreset> {
    return firstValueFrom(this.#http.post<SitePreset>(`${this.#basePath}/site-presets`, body));
  }

  /** Modification partielle : champs absents inchangés (ADR-039). */
  updatePreset(presetId: string, body: SitePresetRequest): Promise<SitePreset> {
    return firstValueFrom(this.#http.put<SitePreset>(`${this.#basePath}/site-presets/${presetId}`, body));
  }

  deletePreset(presetId: string): Promise<void> {
    return firstValueFrom(this.#http.delete<void>(`${this.#basePath}/site-presets/${presetId}`));
  }

  /** Promote this preset to the default for its (organizationId, siteType). */
  setPresetDefault(presetId: string): Promise<SitePreset> {
    return firstValueFrom(this.#http.patch<SitePreset>(`${this.#basePath}/site-presets/${presetId}/default`, {}));
  }
}
